import { apiClient } from './client';
import type { QueryParams } from './client';
import type {
  ApiEnvelope,
  PaginatedData,
  UserWithProfile,
  UserCreate,
  UserUpdate,
  UserRegistration,
  ActivateUser,
  VincularVendedorErpBody,
  VincularVendedorErpResult,
  ChangePassword,
  ResetPassword,
  UsersListParams,
  Profile,
  ProfileUpdateBody,
  Group,
  GroupCreateUpdate,
  GroupsListParams,
  Permission,
  AssignPermissions,
  PermissionsListParams,
  AuditLog,
  AuditLogsListParams,
} from './types';

// ── Usuarios ────────────────────────────────────────────────────────────────

/**
 * Listar usuarios paginados.
 *
 * GET /api/v1/usuarios/
 */
export function listarUsuarios(params?: UsersListParams) {
  return apiClient.get<ApiEnvelope<PaginatedData<UserWithProfile>>>(
    '/api/v1/usuarios/',
    params as QueryParams,
  );
}

/**
 * Ver detalle de un usuario.
 *
 * GET /api/v1/ver-usuario/{id}/
 */
export function verUsuario(id: number | string) {
  return apiClient.get<ApiEnvelope<UserWithProfile>>(
    `/api/v1/ver-usuario/${id}/`,
  );
}

/**
 * Crear un usuario (requiere permisos de administrador).
 *
 * POST /api/v1/crear-usuario/
 */
export function crearUsuario(data: UserCreate) {
  return apiClient.post<ApiEnvelope<UserWithProfile>>(
    '/api/v1/crear-usuario/',
    data,
  );
}

/**
 * Registrar usuario de forma pública (sin autenticación).
 *
 * POST /api/v1/registrar-usuario/
 */
export function registrarUsuario(data: UserRegistration) {
  return apiClient.post<ApiEnvelope<UserWithProfile>>(
    '/api/v1/registrar-usuario/',
    data,
    undefined,
    true,
  );
}

/**
 * Actualizar datos de un usuario.
 *
 * PUT /api/v1/actualizar-usuario/{id}/
 */
export function actualizarUsuario(id: number | string, data: UserUpdate) {
  return apiClient.put<ApiEnvelope<UserWithProfile>>(
    `/api/v1/actualizar-usuario/${id}/`,
    data,
  );
}

/**
 * Activar o desactivar un usuario.
 *
 * PUT /api/v1/activar-usuario/{id}/
 */
export function activarUsuario(id: number | string, data: ActivateUser) {
  return apiClient.put<ApiEnvelope<UserWithProfile>>(
    `/api/v1/activar-usuario/${id}/`,
    data,
  );
}

/**
 * Vincular a mano un vendedor del ERP con un vendedor propio del GPS
 * (vendedor_id null = volver al vínculo automático: mismo usuario y cédula).
 *
 * PUT /api/v1/vincular-vendedor-erp/{id}/
 */
export function vincularVendedorErp(id: number | string, data: VincularVendedorErpBody) {
  return apiClient.put<ApiEnvelope<VincularVendedorErpResult>>(
    `/api/v1/vincular-vendedor-erp/${id}/`,
    data,
  );
}

/**
 * Eliminar usuario (operación deshabilitada en el backend, retorna 405).
 *
 * DELETE /api/v1/ver-usuario/{id}/
 */
export function eliminarUsuario(id: number | string) {
  return apiClient.delete<ApiEnvelope<null>>(`/api/v1/ver-usuario/${id}/`);
}

// ── Perfil ──────────────────────────────────────────────────────────────────

/**
 * Consultar el perfil del usuario autenticado.
 *
 * GET /api/v1/perfil-propio/
 */
export function perfilPropio() {
  return apiClient.get<ApiEnvelope<Profile>>('/api/v1/perfil-propio/');
}

/**
 * Actualizar parcialmente el perfil del usuario autenticado.
 *
 * POST /api/v1/actualizar-perfil/
 */
export function actualizarPerfil(data: ProfileUpdateBody) {
  return apiClient.post<ApiEnvelope<Profile>>('/api/v1/actualizar-perfil/', data);
}

/**
 * Cambiar la contraseña del usuario autenticado.
 *
 * POST /api/v1/cambiar-contrasena/
 */
export function cambiarContrasena(data: ChangePassword) {
  return apiClient.post<ApiEnvelope<Profile>>(
    '/api/v1/cambiar-contrasena/',
    data,
  );
}

/**
 * Resetear la contraseña de un usuario (acción de administrador).
 *
 * POST /api/v1/resetear-contrasena/{id}/
 */
export function resetearContrasena(id: number | string, data: ResetPassword) {
  return apiClient.post<ApiEnvelope<null>>(
    `/api/v1/resetear-contrasena/${id}/`,
    data,
  );
}

// ── Grupos ──────────────────────────────────────────────────────────────────

/**
 * Listar grupos paginados.
 *
 * GET /api/v1/grupos/
 */
export function listarGrupos(params?: GroupsListParams) {
  return apiClient.get<ApiEnvelope<PaginatedData<Group>>>(
    '/api/v1/grupos/',
    params as QueryParams,
  );
}

/**
 * Ver detalle de un grupo.
 *
 * GET /api/v1/ver-grupo/{id}/
 */
export function verGrupo(id: number | string) {
  return apiClient.get<ApiEnvelope<Group>>(`/api/v1/ver-grupo/${id}/`);
}

/**
 * Crear un grupo.
 *
 * POST /api/v1/crear-grupo/
 */
export function crearGrupo(data: GroupCreateUpdate) {
  return apiClient.post<ApiEnvelope<Group>>('/api/v1/crear-grupo/', data);
}

/**
 * Actualizar un grupo existente.
 *
 * PUT /api/v1/actualizar-grupo/{id}/
 */
export function actualizarGrupo(
  id: number | string,
  data: GroupCreateUpdate,
) {
  return apiClient.put<ApiEnvelope<Group>>(
    `/api/v1/actualizar-grupo/${id}/`,
    data,
  );
}

/**
 * Eliminar un grupo.
 *
 * DELETE /api/v1/eliminar-grupo/{id}/
 */
export function eliminarGrupo(id: number | string) {
  return apiClient.delete<ApiEnvelope<null>>(
    `/api/v1/eliminar-grupo/${id}/`,
  );
}

// ── Permisos ─────────────────────────────────────────────────────────────────

/**
 * Listar permisos paginados.
 *
 * GET /api/v1/permisos/
 */
export function listarPermisos(params?: PermissionsListParams) {
  return apiClient.get<ApiEnvelope<PaginatedData<Permission>>>(
    '/api/v1/permisos/',
    params as QueryParams,
  );
}

/**
 * Asignar permisos a un usuario o grupo.
 * Pasa `id` para usuario o `group` para grupo en los query params.
 *
 * POST /api/v1/asignar-permisos/
 */
export function asignarPermisos(
  data: AssignPermissions,
  target: { id?: number; group?: number },
) {
  return apiClient.post<ApiEnvelope<null>>(
    '/api/v1/asignar-permisos/',
    data,
    target as QueryParams,
  );
}

// ── Módulos ─────────────────────────────────────────────────────────────────

/**
 * Listar módulos disponibles en el sistema.
 *
 * GET /api/v1/modulos/
 */
export function listarModulos() {
  return apiClient.get<ApiEnvelope<unknown>>('/api/v1/modulos/');
}

// ── Auditoría ────────────────────────────────────────────────────────────────

/**
 * Listar logs de auditoría del tenant actual.
 *
 * GET /api/v1/audit-logs/
 */
export function listarAuditLogs(params?: AuditLogsListParams) {
  return apiClient.get<ApiEnvelope<PaginatedData<AuditLog>>>(
    '/api/v1/audit-logs/',
    params as QueryParams,
  );
}
